import React, { FC, useEffect } from 'react'

import { Title } from '@Components/CMS/Title'
import { ProgressBar } from '@Components/ProgressBar'
import { useTranslation } from '@Globals/Translations'

import styles from './UrgencyTimer.module.scss'
import { useUrgencyTimer } from './useUrgencyTimer'

export enum TimerVisualizationType {
  None = 'None',
  ProgressiveBar = 'Progressive Bar',
}

export interface UrgencyTimerProps {
  endAt: Date
  id?: string
  mainTitle: string
  onDeadlineReached?: () => void
  showCountdown: boolean
  showTitle: boolean
  startAt: Date
  subTitle: string
  visualizationType: TimerVisualizationType
}

export const UrgencyTimer: FC<UrgencyTimerProps> = ({
  endAt,
  id,
  mainTitle,
  onDeadlineReached,
  showCountdown,
  showTitle,
  startAt,
  subTitle,
  visualizationType,
}) => {
  const {
    days,
    deadlineReached,
    hours,
    minutes,
    percentage,
    seconds,
  } = useUrgencyTimer({ endAt, startAt })
  const { t } = useTranslation()

  useEffect(() => {
    if (deadlineReached && onDeadlineReached) {
      onDeadlineReached()
    }
  }, [deadlineReached, onDeadlineReached])

  const daysWordbook = days === 1 ? 'cms:DAY_LABEL' : 'cms:DAYS_LABEL'
  const hoursWordbook = hours === 1 ? 'cms:HOUR_LABEL' : 'cms:HOURS_LABEL'
  const minutesWordbook = minutes === 1 ? 'cms:MINUTE_LABEL' : 'cms:MINUTES_LABEL'
  const secondsWordbook = seconds === 1 ? 'cms:SECOND_LABEL' : 'cms:SECONDS_LABEL'

  const isProgressiveBar = visualizationType === TimerVisualizationType.ProgressiveBar

  return (
    <section className={styles.UrgencyTimerWrapper} id={id}>
      {(showTitle && mainTitle) && <Title subtitle={subTitle} title={mainTitle} type="h2" />}

      <div className={styles.UrgencyContent}>
        {showCountdown && (
          <div className={styles.TimerReadout}>
            {days > 0 && (`${days} ${t(daysWordbook)} `)}
            {(hours > 0 || days > 0) && (`${hours} ${t(hoursWordbook)} `)}
            {deadlineReached ? '0' : minutes.toString()}
            {' '}
            {t(minutesWordbook)}
            {' '}
            {deadlineReached ? '0' : seconds.toString()}
            {' '}
            {t(secondsWordbook)}
          </div>
        )}

        {isProgressiveBar && (
          <ProgressBar progressColor="var(--black)" trackColor="var(--neutral-200)" width={Math.max(0, percentage)} withMarginTop={false} />
        )}
      </div>
    </section>
  )
}
